import { create } from 'zustand';
import type { TradeAccountDto, TradeAccountInput, TradeApplicationSummaryDto } from '@ecom/contracts';
import { adminApi, errorMessage, withSession } from '@/lib/api-client';
import { useUiStore } from '@/i18n';

/**
 * A customer's trade account (ADR-045): the company they buy for and the price
 * list they buy from. Kept apart from the customer store — it is its own
 * endpoint, and a money decision with its own permission.
 */

interface TradeAccountsState {
  customerId: string | null;
  account: TradeAccountDto | null;
  /** Their latest application for one, if they sent any (ADR-046). */
  application: TradeApplicationSummaryDto | null;
  loading: boolean;
  saving: boolean;
  error: string | null;

  load: (customerId: string) => Promise<void>;
  save: (customerId: string, input: TradeAccountInput) => Promise<boolean>;
}

export const useTradeAccountsStore = create<TradeAccountsState>((set) => ({
  customerId: null,
  account: null,
  application: null,
  loading: false,
  saving: false,
  error: null,

  async load(customerId) {
    set({ customerId, account: null, application: null, loading: true, error: null });
    try {
      const [account, applications] = await Promise.all([
        withSession(() => adminApi.trade.account(customerId)),
        withSession(() =>
          adminApi.tradeApplications.list({ customerId, limit: 1, locale: useUiStore.getState().locale }),
        ),
      ]);
      set({ account, application: applications.items[0] ?? null, loading: false });
    } catch (error) {
      set({ loading: false, error: errorMessage(error, 'Could not load the trade account.') });
    }
  },

  async save(customerId, input) {
    set({ saving: true, error: null });
    try {
      const account = await withSession(() => adminApi.trade.saveAccount(customerId, input));
      set({ account, saving: false });
      return true;
    } catch (error) {
      // An invalid CR or VAT number comes back from the API in words.
      set({ saving: false, error: errorMessage(error, 'Could not save the trade account.') });
      return false;
    }
  },
}));
